import { useMemo } from 'react';
import { useTranslate } from 'src/locales';
import { hasRole } from "@heyjob-dash/shared/utils/check-permission";
import { autoAvailable } from "@heyjob-dash/shared/fringe-auto/auto-feature";

import { paths } from 'src/routes/paths';

import Label from 'src/components/label';
import SvgColor from 'src/components/svg-color';

import Iconify from '../../components/iconify';
import { buildStudioCompanyNav } from './config-navigation-company';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

/** Studio Softer: l'unico con l'ambiente Zucchetti collegato (uguale a `payrollId` nella config API). */
const ZUCCHETTI_SYNC_PAYROLL_ID = 2;

const ICONS = {
  job: icon('ic_job'),
  timesheet: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData(user) {
  // const { user } = useAuthContext();

  const { t } = useTranslate();

  const data = useMemo(
    () => [
        // OVERVIEW
        // ----------------------------------------------------------------------
        {
          items: [
            {
              title: t('back'),
              path: paths.dashboard.root,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:round-alt-arrow-left-bold-duotone" />
            }
          ]
        },
        {
          items: [
            {
              title: t('search company'),
              path: paths.dashboard.root,
              icon: ICONS.dashboard
            }
          ]
        },
        // SUPERADMIN
        // ----------------------------------------------------------------------
        {
          subheader: t('superadmin'),
          items: [
            {
              title: t('news'),
              path: paths.dashboard.news,
              icon: ICONS.label
            },
            {
              title: t('popup_notices'),
              path: paths.dashboard.popupNotices,
              icon: ICONS.external
            },
            {
              title: t('faq_e_learning'),
              path: paths.dashboard.faqElearning,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:diploma-verified-bold-duotone" />
            },
            {
              title: t('external_services'),
              path: '#',
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:shield-warning-bold-duotone" />,
              children: [
                {
                  title: t('companies'),
                  path: paths.dashboard.externalLinks
                },
                {
                  title: t('employees'),
                  path: paths.dashboard.employeeExternalLinks
                }
              ]
            },
            {
              title: t('operatori'),
              path: paths.dashboard.consultants,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:user-id-bold-duotone" />
            },
            {
              title: t('managers'),
              path: paths.dashboard.managers,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:case-bold-duotone" />
            },
            {
              title: 'Integrazioni',
              path: '#',
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:plug-circle-bold-duotone" />,
              info: <Label color="warning" variant="soft">ALPHA</Label>,
              children: [
                { title: 'B.Point', path: paths.dashboard.integrations.bpoint },
                { title: 'PagheWEB', path: paths.dashboard.integrations.pagheweb },
                // L'ambiente Zucchetti collegato è quello dello studio Softer: l'API rifiuta gli altri studi.
                ...(user?.data?.payroll?.id === ZUCCHETTI_SYNC_PAYROLL_ID
                  ? [{ title: 'Aziende Zucchetti', path: paths.dashboard.integrations.zucchettiPreview }]
                  : []),
                { title: 'API keys', path: paths.dashboard.integrations.apiKeys },
                { title: 'Connettore timbrature', path: paths.dashboard.integrations.clockingConnector },
              ]
            }
          ]
        },
        // OPERATORE
        // ----------------------------------------------------------------------
        {
          subheader: t('operatore'),
          items: [
            ...(autoAvailable(user, ['Studio', 'Operatore']) ? [{ title: 'Auto', path: paths.dashboard.auto, icon: <Iconify icon="solar:wheel-bold-duotone" width={24} /> }] : []),

            {
              title: t('companies'),
              path: paths.dashboard.companies,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:buildings-bold-duotone" />
            },
            {
              title: t('hiring'),
              path: paths.dashboard.hiring,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:notification-unread-lines-bold-duotone" />
            },
            {
              title: t('terminations'),
              path: paths.dashboard.terminations,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:notification-lines-remove-bold-duotone" />
            },
            {
              title: t('help_desk'),
              path: paths.dashboard.helpDesk,
              icon: ICONS.chat
            },
            {
              title: t('upload_documents'),
              path: paths.dashboard.uploadDocuments,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:add-folder-bold-duotone" />
            },
            ...(hasRole(user, 'Studio') ? [{
              title: t('import'),
              path: paths.dashboard.import,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:import-bold-duotone" />
            }] : []),
            {
              title: t('config'),
              path: paths.dashboard.config,
              icon: <Iconify sx={{ width: 24, height: 24 }} icon="solar:settings-bold-duotone" />,
              ...(hasRole(user, 'Studio') ? {
                children: [
                  { title: 'Generale', path: paths.dashboard.config },
                  { title: 'Onboarding aziende', path: paths.dashboard.onboardingConfiguration },
                  { title: 'Modelli dello studio', path: paths.dashboard.studioTemplates },
                ]
              } : {})
            },
            {
              title: t('letters'),
              path: paths.dashboard.letters,
              icon: ICONS.mail
            }
          ]
        },
        // COMPANY
        // ----------------------------------------------------------------------
        ...buildStudioCompanyNav({ user, t }),
      ],
    [t, user]
  );

  return data;
}
